// Typed endpoint wrappers for docs/api.md. All path knowledge lives here.
import { ApiError, qs, request } from "./client";
import type {
  AdminBinding,
  ArchiveResult,
  DocumentAnchor,
  SpaceArchiveResult,
  AdminMember,
  AdminOverview,
  AuditEntry,
  Binding,
  BindingMode,
  Bucket,
  MoveItemBody,
  SpaceItems,
  Collection,
  CollectionDetail,
  CollectionKind,
  CollectionSummary,
  Comment,
  Connection,
  CreateItem,
  DocumentDto,
  ExternalIdentity,
  Instance,
  Item,
  ItemDetail,
  ItemType,
  Manifest,
  MappingRule,
  Me,
  Member,
  OrgRole,
  OrgSettings,
  RemoteCollection,
  RuleRunResult,
  Space,
  StatusCategory,
  SyncReport,
  UpdateItem,
} from "./types";

export { ApiError, errorMessage, setUnauthorizedHandler } from "./client";
export type * from "./types";

const enc = encodeURIComponent;

export const api = {
  // Auth
  instance: () => request<Instance>("GET", "/api/instance"),
  register: (body: { email: string; password: string; display_name: string; org_name?: string }) =>
    request<Me>("POST", "/api/auth/register", body),
  login: (body: { email: string; password: string }) => request<Me>("POST", "/api/auth/login", body),
  logout: () => request<void>("POST", "/api/auth/logout"),
  me: () => request<Me>("GET", "/api/me", undefined, { quiet401: true }),
  patchMe: (body: { display_name?: string; timezone?: string | null }) => request<Me>("PATCH", "/api/me", body),

  // Org
  members: (orgId: string) => request<{ members: Member[] }>("GET", `/api/orgs/${enc(orgId)}/members`),
  externalIdentities: (orgId: string) =>
    request<{ identities: ExternalIdentity[] }>("GET", `/api/orgs/${enc(orgId)}/external-identities`),
  patchExternalIdentity: (id: string, actor_id: string | null) =>
    request<ExternalIdentity>("PATCH", `/api/external-identities/${enc(id)}`, { actor_id }),
  ghostExternalIdentity: (id: string) =>
    request<ExternalIdentity>("POST", `/api/external-identities/${enc(id)}/ghost`),

  // Spaces & collections
  spaces: (orgId: string, includeArchived = false) =>
    request<{ spaces: Space[] }>(
      "GET",
      `/api/orgs/${enc(orgId)}/spaces${qs({ include_archived: includeArchived || undefined })}`,
    ),
  createSpace: (orgId: string, name: string) =>
    request<Space>("POST", `/api/orgs/${enc(orgId)}/spaces`, { name }),
  /**
   * ASSUMPTION (not in docs/api.md): `PATCH /api/spaces/{id} { name }` → Space.
   * Kept here so only this line changes if the backend picks another route.
   */
  patchSpace: (id: string, body: { name: string }) => request<Space>("PATCH", `/api/spaces/${enc(id)}`, body),
  deleteSpace: (id: string) => request<void>("DELETE", `/api/spaces/${enc(id)}`),
  archiveSpace: (id: string, remote = true) =>
    request<SpaceArchiveResult>("POST", `/api/spaces/${enc(id)}/archive`, { remote }),
  unarchiveSpace: (id: string, remote = true) =>
    request<SpaceArchiveResult>("POST", `/api/spaces/${enc(id)}/unarchive`, { remote }),
  collections: (spaceId: string, includeArchived = false) =>
    request<{ collections: CollectionSummary[] }>(
      "GET",
      `/api/spaces/${enc(spaceId)}/collections${qs({ include_archived: includeArchived || undefined })}`,
    ),
  createCollection: (
    spaceId: string,
    body: { name: string; kind: CollectionKind; statuses?: { name: string; category: StatusCategory }[] },
  ) => request<CollectionDetail>("POST", `/api/spaces/${enc(spaceId)}/collections`, body),
  collection: (id: string) => request<CollectionDetail>("GET", `/api/collections/${enc(id)}`),
  patchCollection: (id: string, body: { name?: string; buckets_enabled?: boolean }) =>
    request<CollectionDetail>("PATCH", `/api/collections/${enc(id)}`, body),
  deleteCollection: (id: string) => request<void>("DELETE", `/api/collections/${enc(id)}`),
  archiveCollection: (id: string, remote = true) =>
    request<ArchiveResult>("POST", `/api/collections/${enc(id)}/archive`, { remote }),
  unarchiveCollection: (id: string, remote = true) =>
    request<ArchiveResult>("POST", `/api/collections/${enc(id)}/unarchive`, { remote }),

  // Items
  items: (
    collectionId: string,
    params: { type?: ItemType; status_category?: StatusCategory; q?: string; include_done?: boolean } = {},
  ) =>
    request<{ items: Item[] }>(
      "GET",
      `/api/collections/${enc(collectionId)}/items${qs(params)}`,
    ),
  createItem: (collectionId: string, body: CreateItem) =>
    request<Item>("POST", `/api/collections/${enc(collectionId)}/items`, body),
  item: (id: string) => request<ItemDetail>("GET", `/api/items/${enc(id)}`),
  patchItem: (id: string, body: UpdateItem) => request<Item>("PATCH", `/api/items/${enc(id)}`, body),
  /**
   * v0.4: the body carries `status_id`, or `status_category`, and/or `bucket_id`.
   * ASSUMPTION (contract lists bucket_id as "also"): a move with only `bucket_id` keeps the status.
   */
  moveItem: (id: string, body: MoveItemBody) => request<Item>("POST", `/api/items/${enc(id)}/move`, body),
  deleteItem: (id: string) => request<void>("DELETE", `/api/items/${enc(id)}`),
  /** Items created from this item's document (relation created_from). */
  createdItems: (id: string) => request<{ items: Item[] }>("GET", `/api/items/${enc(id)}/created-items`),
  /** Delete an item together with a chosen subset of its created items. */
  deleteItemWithCreated: (id: string, deleteItemIds: string[]) =>
    request<void>("POST", `/api/items/${enc(id)}/delete`, { delete_item_ids: deleteItemIds }),
  comments: (id: string) => request<{ comments: Comment[] }>("GET", `/api/items/${enc(id)}/comments`),
  addComment: (id: string, body: string) =>
    request<Comment>("POST", `/api/items/${enc(id)}/comments`, { body }),
  /** v0.4: all items of a space's non-archived collections (+ collection infos and statuses). */
  spaceItems: (
    spaceId: string,
    params: { type?: ItemType; status_category?: StatusCategory; q?: string; include_done?: boolean } = {},
  ) => request<SpaceItems>("GET", `/api/spaces/${enc(spaceId)}/items${qs(params)}`),
  myTasks: (includeDone = false) =>
    request<{ items: Item[] }>("GET", `/api/me/tasks${qs({ include_done: includeDone })}`),

  // Documents
  document: (id: string) => request<DocumentDto>("GET", `/api/documents/${enc(id)}`),
  putDocument: (id: string, markdown: string) =>
    request<DocumentDto>("PUT", `/api/documents/${enc(id)}`, { markdown }),
  /**
   * ASSUMPTION (not in docs/api.md): `GET /api/documents/{id}/anchors` →
   * `{ anchors: { anchor, item_id }[] }`. Resolves to null when the server does not offer it
   * (404/405/501), so callers can degrade gracefully.
   */
  documentAnchors: async (id: string): Promise<DocumentAnchor[] | null> => {
    try {
      const r = await request<{ anchors: DocumentAnchor[] }>("GET", `/api/documents/${enc(id)}/anchors`);
      return Array.isArray(r?.anchors) ? r.anchors : null;
    } catch (e) {
      if (e instanceof ApiError && [404, 405, 501].includes(e.status)) return null;
      throw e;
    }
  },

  // Connectors & connections
  // The SDK manifest calls the id `type`; the UI keys everything by `connector_type`.
  connectors: () =>
    request<{ connectors: (Manifest & { type?: string })[] }>("GET", "/api/connectors").then((r) => ({
      connectors: r.connectors.map((m) => ({ ...m, connector_type: m.connector_type ?? m.type ?? "" })),
    })),
  connections: (orgId: string, all = false) =>
    request<{ connections: Connection[] }>("GET", `/api/orgs/${enc(orgId)}/connections${qs({ all: all || undefined })}`),
  createConnection: (
    orgId: string,
    body: {
      connector_type: string;
      scope: "org" | "user";
      name: string;
      config: Record<string, unknown>;
      credentials: Record<string, unknown>;
    },
  ) => request<Connection>("POST", `/api/orgs/${enc(orgId)}/connections`, body),
  oauthStartUrl: (orgId: string, connectorType: string, scope: "org" | "user", name: string) =>
    `/api/orgs/${enc(orgId)}/connections/oauth/start${qs({ connector_type: connectorType, scope, name })}`,
  testConnection: (id: string) => request<Connection>("POST", `/api/connections/${enc(id)}/test`),
  deleteConnection: (id: string) => request<void>("DELETE", `/api/connections/${enc(id)}`),
  remoteCollections: (id: string) =>
    request<{ collections: RemoteCollection[] }>("GET", `/api/connections/${enc(id)}/remote-collections`),
  importCollections: (
    id: string,
    body: { space_id: string; remote_collection_ids: string[]; mode: BindingMode },
  ) => request<{ collections: CollectionDetail[] }>("POST", `/api/connections/${enc(id)}/import`, body),
  importUsers: (id: string) =>
    request<{ identities: ExternalIdentity[] }>("POST", `/api/connections/${enc(id)}/import-users`),

  // Bindings
  createBinding: (
    collectionId: string,
    body: {
      connection_id: string;
      remote_collection_id: string;
      mode: BindingMode;
      /** v0.4, To Do only: this list becomes that bucket … */
      bucket_id?: string;
      /** … or a new bucket named after the list. */
      new_bucket?: boolean;
    },
  ) => request<Binding>("POST", `/api/collections/${enc(collectionId)}/bindings`, body),
  patchBinding: (id: string, body: Partial<Pick<Binding, "mode" | "status_map">>) =>
    request<Binding>("PATCH", `/api/bindings/${enc(id)}`, body),
  syncBinding: (id: string) => request<SyncReport>("POST", `/api/bindings/${enc(id)}/sync`),
  deleteBinding: (id: string) => request<void>("DELETE", `/api/bindings/${enc(id)}`),

  // Buckets (v0.4)
  buckets: (collectionId: string) =>
    request<{ buckets: Bucket[] }>("GET", `/api/collections/${enc(collectionId)}/buckets`),
  createBucket: (collectionId: string, body: { name: string; position?: number; remote?: boolean }) =>
    request<Bucket>("POST", `/api/collections/${enc(collectionId)}/buckets`, body),
  /** `position` is the target index; the server renumbers the other buckets 0..n-1. */
  patchBucket: (id: string, body: { name?: string; position?: number }) =>
    request<Bucket>("PATCH", `/api/buckets/${enc(id)}`, body),
  /** Items move to `moveTo` (or to no bucket) first. */
  deleteBucket: (id: string, moveTo: string | null) =>
    request<void>("DELETE", `/api/buckets/${enc(id)}${qs({ move_to: moveTo })}`),

  // Admin (v0.2; owner/admin only)
  admin: {
    overview: (orgId: string) => request<AdminOverview>("GET", `/api/orgs/${enc(orgId)}/admin/overview`),
    patchSettings: (orgId: string, body: Partial<OrgSettings>) =>
      request<OrgSettings>("PATCH", `/api/orgs/${enc(orgId)}/admin/settings`, body),
    members: (orgId: string) =>
      request<{ members: AdminMember[] }>("GET", `/api/orgs/${enc(orgId)}/admin/members`),
    addMember: (orgId: string, body: { email: string; display_name: string; role: OrgRole; password?: string }) =>
      request<{ member: AdminMember; temporary_password: string | null }>(
        "POST",
        `/api/orgs/${enc(orgId)}/admin/members`,
        body,
      ),
    patchMember: (
      orgId: string,
      actorId: string,
      body: { role?: OrgRole; display_name?: string; status?: "active" | "suspended" },
    ) => request<AdminMember>("PATCH", `/api/orgs/${enc(orgId)}/admin/members/${enc(actorId)}`, body),
    resetPassword: (orgId: string, actorId: string) =>
      request<{ temporary_password: string }>(
        "POST",
        `/api/orgs/${enc(orgId)}/admin/members/${enc(actorId)}/reset-password`,
      ),
    removeMember: (orgId: string, actorId: string) =>
      request<void>("DELETE", `/api/orgs/${enc(orgId)}/admin/members/${enc(actorId)}`),
    mergeActors: (orgId: string, body: { source_actor_id: string; target_actor_id: string }) =>
      request<AdminMember>("POST", `/api/orgs/${enc(orgId)}/admin/actors/merge`, body),
    rules: (orgId: string) => request<{ rules: MappingRule[] }>("GET", `/api/orgs/${enc(orgId)}/admin/mapping-rules`),
    createRule: (orgId: string, body: Omit<MappingRule, "id">) =>
      request<MappingRule>("POST", `/api/orgs/${enc(orgId)}/admin/mapping-rules`, body),
    patchRule: (id: string, body: Partial<MappingRule>) =>
      request<MappingRule>("PATCH", `/api/admin/mapping-rules/${enc(id)}`, body),
    deleteRule: (id: string) => request<void>("DELETE", `/api/admin/mapping-rules/${enc(id)}`),
    runRules: (orgId: string) =>
      request<RuleRunResult>("POST", `/api/orgs/${enc(orgId)}/admin/mapping-rules/run`),
    bindings: (orgId: string) =>
      request<{ bindings: AdminBinding[] }>("GET", `/api/orgs/${enc(orgId)}/admin/bindings`),
    audit: (orgId: string, params: { limit?: number; before?: string } = {}) =>
      request<{ entries: AuditEntry[] }>("GET", `/api/orgs/${enc(orgId)}/admin/audit${qs(params)}`),
  },
};

/** URLs for websockets (same host; vite proxies /api with ws:true in dev). */
export function wsBase(): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}`;
}
export const eventsSocketUrl = () => `${wsBase()}/api/events`;
/** y-websocket builds `${serverUrl}/${room}`. */
export const documentsServerUrl = () => `${wsBase()}/api/documents`;
export const documentRoom = (docId: string) => `${docId}/ws`;

export type { Collection };
